import {
  Box,
  Button,
  Flex,
  Portal,
  Slide,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  useAuthStore,
  usePwaInstallListener,
  usePwaStore,
} from '@diamond/sol-buyer-context';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import {
  BOTTOM_NAV_HEIGHT,
  shouldShowBuyerBottomNav,
} from '../layouts/bottom-nav/bottom-nav-config';
import { usePwaUpdateNotification } from './usePwaUpdateNotification';

const DISMISS_KEY = 'pwa-install-dismissed-at';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const PRODUCT_DETAIL_MOBILE_ACTION_HEIGHT = 96;
const IOS_INSTALL_TOAST =
  'Ketuk ikon Bagikan di Safari, lalu pilih "Tambah ke Layar Utama".';
const MAC_SAFARI_INSTALL_TOAST =
  'Buka menu File atau Bagikan, lalu pilih "Add to Dock".';

function isIosSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isWebkit = /WebKit/.test(ua);
  const isChrome = /CriOS/.test(ua);
  return isIos && isWebkit && !isChrome;
}

function isMacSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isMacintosh = /Macintosh/.test(ua);
  const isSafari = /Safari/.test(ua);
  const isOtherEngine = /Chrome|Chromium|CriOS|Edg|OPR|Firefox/.test(ua);
  return isMacintosh && isSafari && !isOtherEngine;
}

function isDismissedRecently(): boolean {
  if (typeof window === 'undefined') return true;
  const dismissedAt = localStorage.getItem(DISMISS_KEY);
  if (!dismissedAt) return false;
  return Date.now() - Number(dismissedAt) < DISMISS_COOLDOWN_MS;
}

function getMobileBottom(pathname: string) {
  if (shouldShowBuyerBottomNav(pathname)) {
    return `${BOTTOM_NAV_HEIGHT}px`;
  }

  if (pathname.startsWith('/product/')) {
    return `${PRODUCT_DETAIL_MOBILE_ACTION_HEIGHT}px`;
  }

  return '0';
}

type PwaInstallBannerProps = {
  isPwaEnabled: boolean;
};

export function PwaInstallBanner({ isPwaEnabled }: PwaInstallBannerProps) {
  const router = useRouter();
  const accessToken = useAuthStore((state) => state.access_token);
  const isInstallable = usePwaStore((state) => state.isInstallable);
  const isInstalled = usePwaStore((state) => state.isInstalled);
  const triggerInstallPrompt = usePwaStore(
    (state) => state.triggerInstallPrompt
  );
  const toast = useToast();
  const mobileBottom = getMobileBottom(router.pathname);
  const sitsOnBottomNav = mobileBottom !== '0';

  const [isDismissed, setIsDismissed] = useState(isDismissedRecently);

  usePwaInstallListener(isPwaEnabled);
  usePwaUpdateNotification(isPwaEnabled);

  const isAuthenticated = Boolean(accessToken);
  const showManualSafariInstall =
    (isIosSafari() || isMacSafari()) && !isInstalled;
  const visible =
    isPwaEnabled &&
    isAuthenticated &&
    !isDismissed &&
    !isInstalled &&
    (isInstallable || showManualSafariInstall);

  const handleInstall = useCallback(async () => {
    if (isIosSafari()) {
      toast({
        title: 'Pasang Aplikasi',
        description: IOS_INSTALL_TOAST,
        status: 'info',
        duration: 8000,
        isClosable: true,
      });
      setIsDismissed(true);
      return;
    }

    if (isMacSafari()) {
      toast({
        title: 'Pasang Aplikasi',
        description: MAC_SAFARI_INSTALL_TOAST,
        status: 'info',
        duration: 8000,
        isClosable: true,
      });
      setIsDismissed(true);
      return;
    }

    const outcome = await triggerInstallPrompt();
    if (outcome) {
      setIsDismissed(true);
    }
  }, [toast, triggerInstallPrompt]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setIsDismissed(true);
  }, []);

  if (!visible) return null;

  return (
    <Portal>
      <Slide
        direction="bottom"
        in
        style={{ zIndex: 'var(--chakra-zIndices-overlay)' }}
      >
        <Flex
          position="fixed"
          bottom={{ base: mobileBottom, md: '0' }}
          left={0}
          right={0}
          bg="white"
          borderTopWidth="1px"
          borderColor="gray.200"
          px={{ base: 3, md: 4 }}
          pt={3}
          pb={
            sitsOnBottomNav
              ? 3
              : {
                  base: 'calc(env(safe-area-inset-bottom) + 0.75rem)',
                  md: 3,
                }
          }
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'stretch', md: 'center' }}
          justify="space-between"
          gap={3}
          zIndex="overlay"
          boxShadow="0 -2px 10px rgba(0,0,0,0.1)"
        >
          <Flex align="center" gap={3} flex={1} minW={0}>
            <Text fontSize="xl" lineHeight={1}>
              &#11015;
            </Text>
            <Box minW={0}>
              <Text fontWeight="semibold" fontSize="sm">
                Pasang Sukanda OneLink
              </Text>
              <Text fontSize="xs" color="gray.500" overflowWrap="anywhere">
                Akses lebih cepat langsung dari layar utama
              </Text>
            </Box>
          </Flex>
          <Flex
            gap={2}
            ml={{ base: 0, md: 3 }}
            w={{ base: 'full', md: 'auto' }}
            minW={0}
          >
            <Button
              size="sm"
              variant="ghost"
              minH="44px"
              minW={0}
              flex={{ base: 1, md: 'initial' }}
              onClick={handleDismiss}
            >
              Nanti Saja
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              minH="44px"
              minW={0}
              flex={{ base: 1, md: 'initial' }}
              onClick={handleInstall}
            >
              Pasang
            </Button>
          </Flex>
        </Flex>
      </Slide>
    </Portal>
  );
}
